import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Input, Button, message } from "antd";
import "./Login.css";
import NewLogo from "../../assets/images/logonew.jpg";
import useUserStore from "../../store/useUserStrore";
import usePlanStore from "../../store/usePlanStore";

const Login = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false); // separate loader
  const [verifyingOtp, setVerifyingOtp] = useState(false); // separate loader
  const [otpRequested, setOtpRequested] = useState(false);
  const [timer, setTimer] = useState(30);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const selectedPlan = params.get("plan");
  const { fetchUserPlan } = usePlanStore();
  const { fetchUserDetails } = useUserStore();
  const navigate = useNavigate();

  /* RequireAuth stashes the page it turned someone away from. Signing in
     returns them there rather than to /home, so a shared link to a mould
     still lands on that mould once they have an account. */
  const redirectTo = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ""}`
    : "/home";

  // redirect if already logged in with plan
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (token && selectedPlan) {
      navigate(`/plans?plan=${selectedPlan}`);
    }
  }, [navigate, selectedPlan]);

  // redirect if authenticated
  useEffect(() => {
    const userToken = localStorage.getItem("userToken");
    if (userToken) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, redirectTo]);

  // countdown for resend
  useEffect(() => {
    let interval;
    if (otpRequested && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpRequested, timer]);

  // send / resend otp
  const handleSendOtp = async (e, isResend = false) => {
    if (e) e.preventDefault();
    setSendingOtp(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/v1/sendotp`,
        { email },
        { headers: { "Content-Type": "application/json" } }
      );

      const data = response.data;
      if (data.success) {
        setOtpRequested(true);
        setTimer(30);
        message.success(
          data?.message || data?.msg || "Authentication Code sent successfully"
        );
      } else {
        if (!isResend) {
          navigate("/signup", { state: { email } });
        }
      }
    } catch (error) {
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        "Failed to send Authentication Code. Please try again.";
      message.error(backendMessage);

      if (!isResend) {
        navigate(`/signup?email=${encodeURIComponent(email)}`);
      }
    } finally {
      setSendingOtp(false);
    }
  };

  // verify otp
  const handleVerifyOtp = async () => {
    setVerifyingOtp(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/auth/v1/verifyotp`,
        { email, otp },
        { headers: { "Content-Type": "application/json" } }
      );

      const otpData = response.data;
      if (otpData.status) {
        localStorage.setItem("userToken", otpData.userToken);
        navigate(redirectTo, { replace: true });
        fetchUserDetails();
        fetchUserPlan();
      } else {
        message.error(
          otpData?.message ||
            otpData?.msg ||
            "Invalid Authentication Code. Please try again."
        );
      }
    } catch (error) {
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        "An Error occurred. Please try again.";
      message.error(backendMessage);
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <div className="login-page">
      <div className="container center">
        <div className="new-login-page">
          <div className="right-content">
            <img loading="lazy" src={NewLogo} alt="Tooth Finder" />
            <p className="auth-brand-line">
              Every mould in the catalogue, searchable by its measurements.
            </p>
          </div>
          <div className="left-content">
            {/* Two doors, one card. The vendor flow lives at /vendors with
                its own OTP form and a signup that sets profileType, so this
                routes there rather than duplicating that logic here. */}
            <div className="auth-toggle" role="tablist" aria-label="Account type">
              <button
                type="button"
                role="tab"
                aria-selected="true"
                className="auth-toggle-option auth-toggle-active"
              >
                User
              </button>
              <button
                type="button"
                role="tab"
                aria-selected="false"
                className="auth-toggle-option"
                onClick={() => navigate("/vendors")}
              >
                Vendor
              </button>
            </div>

            <h2>Sign in with Email</h2>
            <p>Find The Ideal Set of Teeth Instantly</p>

            <form onSubmit={(e) => handleSendOtp(e)}>
              <div className="input-group">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={otpRequested} // keep visible but disabled after OTP is sent
                  placeholder="Enter your email"
                />
              </div>

              {!otpRequested && (
                <Button type="primary" htmlType="submit" loading={sendingOtp}>
                  {sendingOtp
                    ? "Sending Authentication Code..."
                    : "Get Started"}
                </Button>
              )}
            </form>

            {/* OTP Section */}
            {otpRequested && (
              <>
                <div className="input-group" style={{ marginTop: 20 }}>
                  <Input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    placeholder="Enter 6-digit Authentication Code"
                  />
                </div>

                <Button
                  type="primary"
                  htmlType="button"
                  loading={verifyingOtp}
                  onClick={handleVerifyOtp}
                  style={{ marginTop: 10 }}
                >
                  {verifyingOtp ? "Verifying..." : "Verify Authentication Code"}
                </Button>

                {/* Resend OTP Section */}
                <div className="auth-resend">
                  {timer > 0 ? (
                    <span>Resend OTP in {timer}s</span>
                  ) : (
                    <Button
                      type="link"
                      htmlType="button"
                      loading={sendingOtp}
                      onClick={(e) => handleSendOtp(e, true)}
                    >
                      {sendingOtp ? "Resending..." : "Resend OTP"}
                    </Button>
                  )}
                </div>
              </>
            )}

            {/* Signup Link */}
            {/* Was an inline #bdb5b5 on #3b82f6, both picked to sit on the
                old dark panel. The panel is light now, so these are classes
                that follow the palette instead. */}
            <div className="auth-alt">
              Don&apos;t Have an Account? <Link to="/signup">Sign up</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
